import React, { memo, useContext, useEffect } from 'react'
import { List, ListItem, colors, Ellipsis, Bleedable } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import deepEqual from 'fast-deep-equal/es6/react'
import { useHistory, useLocation } from '@fs/zion-router'
import { CountsContext } from '../useReservationCounts'
import { GroupsContext } from '../useGroupsState'
import IsNavDrawerHiddenContext from '../IsNavDrawerHiddenContext'

const stickyCss = css`
  position: sticky;
  top: 12px;
`

const drawerCss = css`
  flex-shrink: 0;
  min-width: 208px;
  max-width: 350px;
  background: ${colors.gray00};
  padding: 12px 20px;
  @media print {
    display: none;
  }
`

const groupsCss = css`
  margin-inline-start: var(--spacer-sm);
`

const NavDrawer = ({ canAddPersonalData }) => {
  const [t] = useTranslation()
  const { pathname } = useLocation()
  const { counts, refetchCounts } = useContext(CountsContext)
  const isNavDrawerHidden = useContext(IsNavDrawerHiddenContext)
  const { groups, refetchGroups, currentGroup, setCurrentGroup } = useContext(GroupsContext)
  const history = useHistory()

  const tabLabels = {
    reservations: t('tw-temple.reservations'),
    groups: t('tw-temple.group-reservations'),
    shared: t('tw-temple.shared'),
    'shared-with-temple': t('tw-temple.shared-with-temple'),
    completed: t('tw-temple.completed'),
    'ordinances-ready': t('tw-temple.ordinances-ready'),
    policy: t('tw-temple.reservation-policy-button'),
    scheduleTemple: t('tw-temple.appointment.header'),
  }

  const isPartOfFamilyGroup = groups.length > 0

  useEffect(() => {
    refetchCounts()
  }, [refetchCounts])

  useEffect(() => {
    refetchGroups()
  }, [refetchGroups])

  useEffect(() => {
    if (groups && groups.length > 0 && !currentGroup) {
      setCurrentGroup(groups[0])
    }
  }, [currentGroup, groups, setCurrentGroup])

  return isNavDrawerHidden ? null : (
    <Bleedable top={12} sides={12} css={drawerCss}>
      <div css={stickyCss}>
        <List>
          <ListItem
            badgeCount={(counts && counts?.individualsAndFamiliesNotTempleShared) || 0}
            primaryText={tabLabels.reservations}
            data-testid="reservations-tab-button"
            to="/reservations"
            linkName="navigate:reservations"
            emphasized={pathname === '/reservations'}
          />

          {isPartOfFamilyGroup && canAddPersonalData && (
            <>
              <ListItem
                primaryText={tabLabels.groups}
                data-testid="groups-tab-button"
                to="/groups"
                linkName="navigate:groups"
                emphasized={pathname.startsWith('/groups')}
              />
              {groups?.length > 0 && (
                <List>
                  {groups.map((group) => (
                    <ListItem
                      primaryText={<Ellipsis css={groupsCss}>{group.name}</Ellipsis>}
                      linkName="navigate:specific-group"
                      data-testid={`group-button-${group.groupId}`}
                      // Use "onClick" over "to" so selected state can be set
                      onClick={group.groupId ? () => history.push(`/groups/${group.groupId}`) : undefined}
                      key={group.groupId}
                      as="div"
                      selected={pathname.startsWith('/groups') && currentGroup?.groupId === group.groupId}
                    />
                  ))}
                </List>
              )}
            </>
          )}

          <ListItem
            badgeCount={(counts && counts?.individualsAndFamiliesTempleShared) || 0}
            primaryText={isPartOfFamilyGroup ? tabLabels['shared-with-temple'] : tabLabels.shared}
            data-testid="shared-tab-button"
            to="/shared"
            linkName="navigate:shared"
            emphasized={pathname === '/shared'}
          />

          <ListItem
            badgeCount={(counts && counts?.completedOrdinances) || 0}
            primaryText={tabLabels.completed}
            data-testid="completed-tab-button"
            to="/completed"
            linkName="navigate:completed"
            emphasized={pathname === '/completed'}
          />

          <ListItem
            primaryText={tabLabels['ordinances-ready']}
            to="/ordinances-ready"
            data-testid="ordinances-ready-button"
            linkName="navigate:ordinance-ready"
            emphasized={pathname === '/ordinances-ready'}
          />

          <ListItem
            primaryText={tabLabels.scheduleTemple}
            to="/appointment"
            linkName="navigate:ordinance-ready"
            data-testid="temple-appointment-button"
            emphasized={pathname === '/appointment'}
          />

          <ListItem
            primaryText={tabLabels.policy}
            data-testid="reservation-policy-link"
            to="https://www.familysearch.org/help/helpcenter/article/individuals-for-whom-i-can-request-temple-ordinances"
            external
            target="_blank"
            linkName="navigate:policy"
          />
        </List>
      </div>
    </Bleedable>
  )
}

export default memo(NavDrawer, (oldProps, newProps) => deepEqual(oldProps, newProps))
